import { motion } from "framer-motion";
import { Toaster } from "sonner";
import { ArrowRight, Phone, Shield, Home, Star } from "lucide-react";
import SEO from "@/components/SEO";
import MobileOfferHero from "@/components/MobileOfferHero";
import ResidentialTintForm from "@/components/ResidentialTintForm";

const LOGO = "https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/rzh2dyxf_cropped-928_Logo-281x300.png";

/**
 * Shared residential-tint landing layout — one source of truth for two pages:
 *  - /lp/residential-tint            (Meta ads: minimal header, noindex, source "Meta Ads — Residential Tint LP")
 *  - /services/residential-window-tint (main website: site nav/footer, indexable, source "Website — Residential Tint", FAQ below)
 *
 * Props:
 *  source        referral_source tag saved on the lead
 *  lpKey         analytics `lp` param value (keeps Meta vs website leads separate in reports)
 *  trackLocation data-track-location prefix
 *  testidPrefix  data-testid prefix ("lp" for ads LP, "svc-res" for website)
 *  seo           { title, description, path, noindex, ogImage }
 *  showAdsHeader render the minimal sticky ads header (LP only — website uses the site Navbar)
 *  mobileOfferHero render the mobile-only $150-offer hero (LP only; desktop hero untouched)
 *  children      extra sections rendered below the hero (website FAQ)
 */
export default function ResidentialTintLanding({
  source,
  lpKey,
  trackLocation,
  testidPrefix,
  seo,
  showAdsHeader = false,
  mobileOfferHero = false,
  children,
}) {
  const formId = `${testidPrefix}-form`;

  const scrollToForm = () => {
    document.getElementById(formId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid={`page-${trackLocation}`} className="min-h-screen bg-[var(--bg-2)]">
      <SEO {...seo} />
      <Toaster richColors position="top-right" />

      {/* Minimal ads-LP top bar — only on /lp/ (website version uses the site Navbar) */}
      {showAdsHeader && (
        <header className="sticky top-0 inset-x-0 z-40 bg-white/85 backdrop-blur-xl border-b border-neutral-200">
          <div className="wrap flex items-center justify-between h-[64px]">
            <div className="flex items-center gap-2.5">
              <img src={LOGO} alt="928 Wraps" className="h-8 w-auto" />
              <span className="hidden sm:inline-block font-mono text-[10px] tracking-[0.3em] pl-2.5 border-l border-neutral-200 text-neutral-500">PRESCOTT VALLEY · AZ</span>
            </div>
            <a
              href="tel:+19282730784"
              data-testid={`${testidPrefix}-phone`}
              data-track="phone_click"
              data-track-location={`${trackLocation}-nav`}
              className="flex items-center gap-2 text-sm font-semibold text-neutral-800 hover:text-black"
            >
              <Phone size={16} className="text-[var(--red)]" />
              (928) 273-0784
            </a>
          </div>
        </header>
      )}

      {/* HERO */}
      <section className={`${showAdsHeader ? "pt-6 md:pt-20" : "pt-[104px] md:pt-36"} pb-16 relative overflow-hidden`}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-red-500/5 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] rounded-full bg-amber-400/10 blur-3xl"></div>
        </div>
        <div className="wrap grid md:grid-cols-12 gap-10 relative">
          {/* Mobile-only offer hero (Meta LP) — desktop/tablet never see this */}
          {mobileOfferHero && (
            <MobileOfferHero formId={formId} trackLocation={trackLocation} testidPrefix={testidPrefix} />
          )}
          {/* On mobile: form appears FIRST (above the fold) unless the offer hero is active. On desktop: hero copy left, form right. */}
          <div className={`md:col-span-7 order-2 md:order-1 ${mobileOfferHero ? "hidden md:block" : ""}`}>
            <div className="eyebrow eyebrow-red">Residential Window Tint · Prescott, AZ</div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="h-display text-5xl md:text-7xl uppercase mt-4 leading-[0.95]"
            >
              Cool your home.<br/>
              <span className="italic font-normal">Stop</span> the fade.<br/>
              Cut the bill.
            </motion.h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-xl leading-relaxed">
              Ceramic window film for Prescott Valley, Prescott & Chino Valley homes. Blocks up to <strong className="text-neutral-900">99% of UV</strong>, drops cabin temps by <strong className="text-neutral-900">15–25°F</strong>, and typically pays for itself in <strong className="text-neutral-900">3–5 years</strong> of lower cooling bills.
            </p>

            {/* Trust ribbon */}
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-neutral-500">
              <div className="flex items-center gap-2">
                <Star size={16} className="text-amber-500 fill-amber-500" />
                <span>5.0 on Google · Real reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield size={16} className="text-[var(--red)]" />
                <span>Lifetime film warranty</span>
              </div>
              <div className="flex items-center gap-2">
                <Home size={16} className="text-neutral-700" />
                <span>Local · Owner-operated · Since 2012</span>
              </div>
            </div>

            <div className="mt-10 hidden md:flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={scrollToForm}
                data-testid={`${testidPrefix}-hero-cta`}
                data-track="lp_cta_click"
                data-track-location={`${trackLocation}-hero`}
                className="btn-red text-base"
              >
                Get My FREE Estimate <ArrowRight size={16}/>
              </button>
              <a
                href="tel:+19282730784"
                data-testid={`${testidPrefix}-hero-phone`}
                data-track="phone_click"
                data-track-location={`${trackLocation}-hero`}
                className="btn-ghost text-base"
              >
                <Phone size={16}/> (928) 273-0784
              </a>
            </div>
          </div>

          {/* Inline form — mobile: above the fold (order-1, or below the offer hero when active). Desktop: right side (order-2). scroll-mt clears the sticky header when CTAs scroll to it. */}
          <div id={formId} className={`md:col-span-5 ${mobileOfferHero ? "order-2" : "order-1"} md:order-2 scroll-mt-24`}>
            <ResidentialTintForm source={source} lpKey={lpKey} testidPrefix={testidPrefix} />
          </div>
        </div>
      </section>

      {children}
    </div>
  );
}
